import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Generate or get device ID from localStorage
const getDeviceId = () => {
  let deviceId = localStorage.getItem('portal_device_id');
  if (!deviceId) {
    deviceId = 'device_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
    localStorage.setItem('portal_device_id', deviceId);
  }
  return deviceId;
};

const Portal = () => {
  const [step, setStep] = useState('phone'); // phone, form, success, status
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [existingRequest, setExistingRequest] = useState(null);
  const [requestCode, setRequestCode] = useState('');
  const [statusData, setStatusData] = useState(null);
  const [location, setLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    full_name: '',
    address: '',
    district: '',
    contact_time: '',
    note: ''
  });

  // Time options for contact time picker
  const timeOptions = [
    '09:00 - 12:00',
    '12:00 - 15:00',
    '15:00 - 18:00',
    '18:00 - 21:00',
    'İstənilən vaxt'
  ];

  // Get user location
  const getLocation = () => {
    if (!navigator.geolocation) {
      return;
    }
    
    setLocationLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy
        });
        setLocationLoading(false);
      },
      (error) => {
        console.error('Location error:', error);
        setLocationLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // Check device on mount and request location
  useEffect(() => {
    checkDevice();
    getLocation();
  }, []);

  const checkDevice = async () => {
    try {
      const deviceId = getDeviceId();
      const response = await axios.post(`${API_URL}/api/portal/check-device`, {
        device_id: deviceId
      });
      
      if (response.data.exists) {
        setExistingRequest(response.data);
        setStep('device_blocked');
      }
    } catch (err) {
      console.error('Device check error:', err);
    }
  };

  const handlePhoneCheck = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/portal/check-phone`, {
        phone: phone
      });

      if (response.data.exists) {
        setExistingRequest(response.data);
        setStep('phone_exists');
      } else {
        setStep('form');
      }
    } catch (err) {
      setError('Xəta baş verdi. Yenidən cəhd edin.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/portal/submit`, {
        phone: phone,
        ...formData,
        device_id: getDeviceId(),
        location: location ? {
          latitude: location.latitude,
          longitude: location.longitude,
          accuracy: location.accuracy
        } : null
      });

      if (response.data.success) {
        setRequestCode(response.data.request_code);
        setStep('success');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Xəta baş verdi. Yenidən cəhd edin.');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusCheck = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const code = requestCode.toUpperCase();
      const response = await axios.get(`${API_URL}/api/portal/status/${code}`);
      setStatusData(response.data);
      setStep('status_result');
    } catch (err) {
      setError('Müraciət tapılmadı. Kodu yoxlayın.');
    } finally {
      setLoading(false);
    }
  };

  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 5) return `${numbers.slice(0, 2)} ${numbers.slice(2)}`;
    if (numbers.length <= 7) return `${numbers.slice(0, 2)} ${numbers.slice(2, 5)} ${numbers.slice(5)}`;
    return `${numbers.slice(0, 2)} ${numbers.slice(2, 5)} ${numbers.slice(5, 7)} ${numbers.slice(7, 9)}`;
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-100 text-blue-800',
      verifying: 'bg-yellow-100 text-yellow-800',
      scheduled: 'bg-purple-100 text-purple-800',
      planned: 'bg-purple-100 text-purple-800',
      in_progress: 'bg-blue-100 text-orange-800',
      completed: 'bg-green-100 text-green-800',
      not_connected: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white shadow-md border-b border-gray-100">
        <div className="max-w-lg mx-auto px-4 py-4">
          <div className="flex items-center justify-center">
            <img 
              src="https://www.fiber.net.az/assets/uploads/2022/06/logo-04.png" 
              alt="Fibernet" 
              className="h-12"
            />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-lg mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-lg p-6">
          
          {/* Step: Phone Entry */}
          {step === 'phone' && (
            <>
              <h1 className="text-2xl font-bold text-gray-800 mb-2 text-center">
                Qoşulma müraciəti
              </h1>
              <p className="text-gray-500 text-center mb-6">
                Fibernet şəbəkəsinə qoşulmaq üçün məlumatlarınızı daxil edin
              </p>

              <form onSubmit={handlePhoneCheck}>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Telefon nömrəsi
                </label>
                <div className="flex items-center border-2 border-gray-200 rounded-xl focus-within:border-blue-600 transition-colors">
                  <span className="pl-4 pr-2 text-gray-500">+994</span>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(formatPhone(e.target.value))}
                    placeholder="50 123 45 67"
                    className="flex-1 py-4 pr-4 outline-none text-lg"
                    maxLength={14}
                    required
                  />
                </div>

                {error && (
                  <p className="mt-3 text-red-500 text-sm">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={loading || phone.replace(/\D/g, '').length < 9}
                  className="w-full mt-6 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white font-semibold py-4 rounded-xl transition-colors"
                >
                  {loading ? 'Yoxlanılır...' : 'Davam et'}
                </button>
              </form>

              <div className="mt-6 pt-6 border-t">
                <button
                  onClick={() => setStep('status_check')}
                  className="w-full text-blue-700 font-medium py-2"
                >
                  Müraciət statusunu yoxla
                </button>
              </div>
            </>
          )}

          {/* Step: Phone Exists */}
          {step === 'phone_exists' && existingRequest && (
            <>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-gray-800 mb-2">
                  Mövcud müraciət var
                </h2>
                <p className="text-gray-500">
                  Bu nömrə ilə aktiv müraciət mövcuddur
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-gray-500">Müraciət kodu</span>
                  <span className="font-bold text-gray-800">{existingRequest.request_code}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Status</span>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(existingRequest.status)}`}>
                    {existingRequest.status_label}
                  </span>
                </div>
              </div>

              <button
                onClick={() => {
                  setPhone('');
                  setExistingRequest(null);
                  setStep('phone');
                }}
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold py-4 rounded-xl transition-colors"
              >
                Başqa nömrə ilə davam et
              </button>
            </>
          )}

          {/* Step: Device Blocked */}
          {step === 'device_blocked' && existingRequest && (
            <>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-gray-800 mb-2">
                  Müraciət mövcuddur
                </h2>
                <p className="text-gray-500">
                  Bu cihazdan artıq müraciət göndərilmişdir
                </p>
              </div>

              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-gray-500">Müraciət kodu</span>
                  <span className="font-bold text-gray-800">{existingRequest.request_code}</span>
                </div>
                <div className="flex justify-between items-center mb-3">
                  <span className="text-gray-500">Telefon</span>
                  <span className="font-medium text-gray-800">***{existingRequest.phone}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Status</span>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(existingRequest.status)}`}>
                    {existingRequest.status_label}
                  </span>
                </div>
              </div>

              <button
                onClick={() => setStep('status_check')}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 rounded-xl transition-colors"
              >
                Status yoxla
              </button>
            </>
          )}

          {/* Step: Form */}
          {step === 'form' && (
            <>
              <h1 className="text-2xl font-bold text-gray-800 mb-6 text-center">
                Məlumatlarınız
              </h1>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ad, Soyad *
                  </label>
                  <input
                    type="text"
                    value={formData.full_name}
                    onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                    className="w-full border-2 border-gray-200 rounded-xl py-3 px-4 focus:border-blue-600 outline-none transition-colors"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ünvan *
                  </label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    placeholder="Küçə, bina, mənzil"
                    className="w-full border-2 border-gray-200 rounded-xl py-3 px-4 focus:border-blue-600 outline-none transition-colors"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rayon
                  </label>
                  <input
                    type="text"
                    value={formData.district}
                    onChange={(e) => setFormData({...formData, district: e.target.value})}
                    className="w-full border-2 border-gray-200 rounded-xl py-3 px-4 focus:border-blue-600 outline-none transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Əlaqə vaxtı
                  </label>
                  <select
                    value={formData.contact_time}
                    onChange={(e) => setFormData({...formData, contact_time: e.target.value})}
                    className="w-full border-2 border-gray-200 rounded-xl py-3 px-4 focus:border-blue-600 outline-none transition-colors bg-white"
                  >
                    <option value="">Seçin...</option>
                    {timeOptions.map((time) => (
                      <option key={time} value={time}>{time}</option>
                    ))}
                  </select>
                </div>

                {/* Location indicator */}
                {location && (
                  <div className="flex items-center text-sm text-green-600 bg-green-50 px-3 py-2 rounded-lg">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Məkan məlumatı alındı
                  </div>
                )}
                {locationLoading && (
                  <div className="flex items-center text-sm text-gray-500">
                    <svg className="animate-spin w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Məkan alınır...
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Qeyd
                  </label>
                  <textarea
                    value={formData.note}
                    onChange={(e) => setFormData({...formData, note: e.target.value})}
                    rows={3}
                    className="w-full border-2 border-gray-200 rounded-xl py-3 px-4 focus:border-blue-600 outline-none transition-colors resize-none"
                  />
                </div>

                {error && (
                  <p className="text-red-500 text-sm">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white font-semibold py-4 rounded-xl transition-colors"
                >
                  {loading ? 'Göndərilir...' : 'Müraciət göndər'}
                </button>

                <button
                  type="button"
                  onClick={() => setStep('phone')}
                  className="w-full text-gray-500 font-medium py-2"
                >
                  Geri
                </button>
              </form>
            </>
          )}

          {/* Step: Success */}
          {step === 'success' && (
            <>
              <div className="text-center mb-6">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">
                  Müraciətiniz qəbul edildi!
                </h2>
                <p className="text-gray-500">
                  Tezliklə sizinlə əlaqə saxlanılacaq
                </p>
              </div>

              <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6 text-center mb-6">
                <p className="text-sm text-blue-700 mb-2">Müraciət kodunuz</p>
                <p className="text-3xl font-bold text-blue-700">{requestCode}</p>
                <p className="text-xs text-blue-600 mt-2">Bu kodu yadda saxlayın</p>
              </div>

              <button
                onClick={() => {
                  setRequestCode(requestCode);
                  setStep('status_check');
                }}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 rounded-xl transition-colors"
              >
                Status yoxla
              </button>
            </>
          )}

          {/* Step: Status Check */}
          {step === 'status_check' && (
            <>
              <h1 className="text-2xl font-bold text-gray-800 mb-2 text-center">
                Status yoxla
              </h1>
              <p className="text-gray-500 text-center mb-6">
                Müraciət kodunuzu daxil edin
              </p>

              <form onSubmit={handleStatusCheck}>
                <input
                  type="text"
                  value={requestCode}
                  onChange={(e) => setRequestCode(e.target.value.toUpperCase())}
                  placeholder="FN-0001"
                  className="w-full border-2 border-gray-200 rounded-xl py-4 px-4 text-center text-xl font-bold focus:border-blue-600 outline-none transition-colors uppercase"
                  required
                />

                {error && (
                  <p className="mt-3 text-red-500 text-sm text-center">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={loading || !requestCode}
                  className="w-full mt-6 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white font-semibold py-4 rounded-xl transition-colors"
                >
                  {loading ? 'Yoxlanılır...' : 'Yoxla'}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setRequestCode('');
                    setError('');
                    setStep('phone');
                  }}
                  className="w-full mt-3 text-gray-500 font-medium py-2"
                >
                  Yeni müraciət
                </button>
              </form>
            </>
          )}

          {/* Step: Status Result */}
          {step === 'status_result' && statusData && (
            <>
              <div className="text-center mb-6">
                <h2 className="text-xl font-bold text-gray-800 mb-4">
                  Müraciət #{statusData.request_code}
                </h2>
                <div className={`inline-block px-6 py-3 rounded-full text-lg font-semibold ${getStatusColor(statusData.status)}`}>
                  {statusData.status_label}
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-4 space-y-3 mb-6">
                <div className="flex justify-between">
                  <span className="text-gray-500">Ad</span>
                  <span className="font-medium text-gray-800">{statusData.customer_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Ünvan</span>
                  <span className="font-medium text-gray-800 text-right max-w-[200px]">{statusData.address}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Yaradılma tarixi</span>
                  <span className="font-medium text-gray-800">
                    {new Date(statusData.created_at).toLocaleDateString('az-AZ')}
                  </span>
                </div>
              </div>

              {/* Status Timeline */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Status gedişatı</h3>
                <div className="space-y-2">
                  {['new', 'verifying', 'scheduled', 'in_progress', 'completed'].map((s, index) => {
                    const statuses = ['new', 'verifying', 'scheduled', 'in_progress', 'completed'];
                    const currentIndex = statuses.indexOf(statusData.status);
                    const isCompleted = index <= currentIndex;
                    const isCurrent = s === statusData.status;
                    const labels = {
                      new: 'Yeni',
                      verifying: 'Yoxlanılır',
                      scheduled: 'Planlanıb',
                      in_progress: 'İcrada',
                      completed: 'Tamamlanmış'
                    };
                    
                    return (
                      <div key={s} className="flex items-center">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-3 ${
                          isCompleted ? 'bg-green-500' : 'bg-gray-200'
                        }`}>
                          {isCompleted && (
                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                        <span className={`${isCurrent ? 'font-semibold text-gray-800' : 'text-gray-500'}`}>
                          {labels[s]}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <button
                onClick={() => {
                  setStatusData(null);
                  setRequestCode('');
                  setStep('phone');
                }}
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold py-4 rounded-xl transition-colors"
              >
                Əsas səhifə
              </button>
            </>
          )}

        </div>

        {/* Footer */}
        <div className="text-center mt-6 text-slate-500 text-sm">
          © 2026 Fibernet. Bütün hüquqlar qorunur.
        </div>
      </main>
    </div>
  );
};

export default Portal;
